/**
 * SvgOverlay — renders a single svg_overlay() declaration as an absolutely-
 * positioned HTML element on top of the main viewer canvas.
 *
 * The SVG is rendered inline (dangerouslySetInnerHTML) so that CSS `color`
 * inheritance reaches `currentColor` references inside the SVG markup.
 * When `overlay.color` is set, it is applied as the CSS `color` property on
 * the wrapper div and the SVG's `currentColor` references will pick it up.
 *
 * Position and size follow the same fraction/pixel convention as PiPPanel:
 *   |value| ≤ 1  → fraction of viewer dimension (CSS %)
 *   |value| > 1  → fixed pixels (positive = from left/top, negative = from right/bottom)
 *
 * When height = 0 in the source, the CSS `aspect-ratio` property is used to
 * let the browser derive the height from the SVG's natural aspect ratio.
 * When an explicit height is provided, the SVG is stretched to fill the box.
 */

import { useMemo } from 'react';
import type { SvgOverlay as SvgOverlayType } from '@/worker/types';
import type { ProjectFile } from '@/components/files/FileManager';

interface SvgOverlayProps {
  overlay: SvgOverlayType;
  projectFiles: ProjectFile[];
}

/**
 * Parse the natural width and height from an SVG string.
 * Checks explicit width/height attributes first, then falls back to viewBox.
 * Returns null if neither can be found.
 */
function parseSvgAspectRatio(svgContent: string): number | null {
  // 1. Explicit width/height attributes (e.g. width="512" or width="512px")
  const wMatch = svgContent.match(/<svg[^>]*\swidth=["']([0-9.]+)(?:px)?["']/i);
  const hMatch = svgContent.match(
    /<svg[^>]*\sheight=["']([0-9.]+)(?:px)?["']/i,
  );
  if (wMatch && hMatch) {
    const w = parseFloat(wMatch[1]);
    const h = parseFloat(hMatch[1]);
    if (w > 0 && h > 0) return w / h;
  }

  // 2. viewBox="minX minY width height"
  const vbMatch = svgContent.match(/<svg[^>]*\sviewBox=["']([0-9.\s-]+)["']/i);
  if (vbMatch) {
    const parts = vbMatch[1].trim().split(/\s+/);
    if (parts.length === 4) {
      const w = parseFloat(parts[2]);
      const h = parseFloat(parts[3]);
      if (w > 0 && h > 0) return w / h;
    }
  }

  return null;
}

export function SvgOverlay({ overlay, projectFiles }: SvgOverlayProps) {
  // Look up the SVG file content
  const file = projectFiles.find((f) => f.filename === overlay.file);
  const svgContent =
    file && typeof file.content === 'string' ? file.content : null;

  // Parse the natural aspect ratio from the SVG markup
  const aspectRatio = useMemo(
    () => (svgContent ? parseSvgAspectRatio(svgContent) : null),
    [svgContent],
  );

  if (!svgContent) return null;

  // ── CSS position ──────────────────────────────────────────────────────────
  // Pixel mode (|value| > 1): use px units directly.
  // Fraction mode (|value| ≤ 1): use % of the container.
  // Negative = anchored from the opposite edge (right/bottom).
  const left =
    overlay.xPx !== undefined
      ? overlay.xPx >= 0
        ? `${overlay.xPx}px`
        : undefined
      : overlay.x >= 0
        ? `${overlay.x * 100}%`
        : undefined;

  const right =
    overlay.xPx !== undefined
      ? overlay.xPx < 0
        ? `${-overlay.xPx}px`
        : undefined
      : overlay.x < 0
        ? `${-overlay.x * 100}%`
        : undefined;

  const top =
    overlay.yPx !== undefined
      ? overlay.yPx >= 0
        ? `${overlay.yPx}px`
        : undefined
      : overlay.y >= 0
        ? `${overlay.y * 100}%`
        : undefined;

  const bottom =
    overlay.yPx !== undefined
      ? overlay.yPx < 0
        ? `${-overlay.yPx}px`
        : undefined
      : overlay.y < 0
        ? `${-overlay.y * 100}%`
        : undefined;

  const width =
    overlay.widthPx !== undefined
      ? `${overlay.widthPx}px`
      : `${overlay.width * 100}%`;

  // Height: explicit value overrides aspect-ratio; height=0 → use CSS aspect-ratio
  const useAspectRatio = overlay.height === 0 && overlay.heightPx === undefined;
  const height =
    overlay.heightPx !== undefined
      ? `${overlay.heightPx}px`
      : overlay.height !== 0
        ? `${overlay.height * 100}%`
        : undefined; // let aspect-ratio drive it

  return (
    <div
      style={{
        position: 'absolute',
        left,
        right,
        top,
        bottom,
        width,
        height,
        // Derive height from SVG natural proportions when no explicit height given
        aspectRatio:
          useAspectRatio && aspectRatio !== null
            ? String(aspectRatio)
            : undefined,
        // Color injection: currentColor in the SVG will inherit this value
        color: overlay.color || undefined,
        opacity: overlay.opacity !== 1 ? overlay.opacity : undefined,
        // Overflow hidden so inline SVG doesn't bleed outside the box
        overflow: 'hidden',
        // Pointer events disabled — overlays are purely decorative
        pointerEvents: 'none',
      }}
      // Render the SVG inline so CSS `color` inheritance reaches currentColor
      dangerouslySetInnerHTML={{ __html: svgContent }}
    />
  );
}
